import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Calendar, Clock, MapPin, Users, Mail, Phone, User,
  AlertCircle, CheckCircle, ExternalLink, ArrowLeft,
  Video, FileText
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import api from '../../services/apiService';

interface EventDetails {
  id: number;
  type: string;
  title: string;
  description: string;
  event_type: string;
  start_datetime: string;
  end_datetime: string;
  all_day: boolean;
  location: string;
  organizer: string;
  meeting_link: string;
  agenda: string;
  formatted_date_range: string;
  registration_count: number;
}

interface RegistrationForm {
  participant_name: string;
  participant_email: string;
  participant_phone: string;
  date_of_birth: string;
  guardian_name: string;
  guardian_phone: string;
  medical_conditions: string;
  emergency_contact: string;
  notes: string;
}

const EventDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [form, setForm] = useState<RegistrationForm>({
    participant_name: '',
    participant_email: '',
    participant_phone: '',
    date_of_birth: '',
    guardian_name: '',
    guardian_phone: '',
    medical_conditions: '',
    emergency_contact: '',
    notes: '',
  });

  const loadEventDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/schedule/event/event/${id}`);
      setEvent(response.data);
    } catch (error) {
      console.error('Error loading event details:', error);
      toast.error('Failed to load event details');
      navigate('/schedule');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadEventDetails();
    }
  }, [id]);

  const calculateAge = (birthDate: string): number => {
    if (!birthDate) return 0;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const isMinor = calculateAge(form.date_of_birth) < 18;
  const isUpcoming = event ? new Date(event.start_datetime) > new Date() : false;
  const isOngoing = event ? 
    new Date(event.start_datetime) <= new Date() && new Date(event.end_datetime) >= new Date() : false;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!event) return;

    try {
      setRegistering(true);
      
      const registrationData = {
        ...form,
        is_minor: isMinor,
      };

      await api.post(`/api/schedule/register/event/${event.id}`, registrationData);
      
      toast.success('Registration successful! You will receive a confirmation email shortly.');
      setShowRegistrationForm(false);
      setForm({
        participant_name: '',
        participant_email: '',
        participant_phone: '',
        date_of_birth: '',
        guardian_name: '',
        guardian_phone: '',
        medical_conditions: '',
        emergency_contact: '',
        notes: '',
      });
      
      // Reload event details to get updated registration count
      await loadEventDetails();
    } catch (error: any) {
      console.error('Registration error:', error);
      const message = error.response?.data?.error || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setRegistering(false);
    }
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEventStatusBadge = () => {
    if (!event) return null;
    
    if (isOngoing) {
      return (
        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
          Ongoing
        </span>
      );
    } else if (isUpcoming) {
      return (
        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
          Upcoming
        </span>
      );
    } else {
      return (
        <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
          Past Event
        </span>
      );
    }
  };

  if (loading) {
    return (
      <>
        <div className="flex justify-center items-center min-h-screen">
          <LoadingSpinner size="large" />
        </div>
      </>
    );
  }

  if (!event) {
    return (
      <>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Event Not Found</h2>
          <button
            onClick={() => navigate('/schedule')}
            className="text-blue-600 hover:text-blue-700"
          >
            Return to Schedule
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/schedule')}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Schedule
          </button>
          
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
              {event.event_type}
            </span>
            {getEventStatusBadge()}
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{event.title}</h1>
          <p className="text-gray-600 text-lg">{event.description}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Event Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Event Details</h2>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <Calendar className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {event.formatted_date_range}
                    </p>
                    {event.all_day && (
                      <p className="text-gray-600">All Day Event</p>
                    )}
                  </div>
                </div>
                
                {event.location && (
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">{event.location}</p>
                    </div>
                  </div>
                )}

                {event.organizer && (
                  <div className="flex items-start">
                    <User className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">Organized by</p>
                      <p className="text-gray-600">{event.organizer}</p>
                    </div>
                  </div>
                )}

                {event.meeting_link && (
                  <div className="flex items-start">
                    <Video className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">Online Meeting</p>
                      {isUpcoming || isOngoing ? (
                        <a
                          href={event.meeting_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        >
                          Join Meeting
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      ) : (
                        <p className="text-gray-500">Meeting link available during event</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-start">
                  <Users className="w-5 h-5 text-gray-400 mt-1 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">Registered Participants</p>
                    <p className="text-gray-600">{event.registration_count} people registered</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Agenda */}
            {event.agenda && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Agenda</h2>
                <div className="prose prose-gray max-w-none">
                  {event.agenda.split('\n').map((line, index) => (
                    <p key={index} className="text-gray-700 mb-2">{line}</p>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Registration */}
            {isUpcoming && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Event Registration</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    <span className="font-medium">Registration Available</span>
                  </div>
                  
                  <p className="text-sm text-gray-600">
                    Register to receive event updates and reminders.
                  </p>
                  
                  <button
                    onClick={() => setShowRegistrationForm(true)}
                    className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Register for Event
                  </button>
                </div>
              </div>
            )}

            {/* Quick Info */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Info</h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Event Type:</span>
                  <span className="font-medium">{event.event_type}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Duration:</span>
                  <span className="font-medium">
                    {Math.round(
                      (new Date(event.end_datetime).getTime() - new Date(event.start_datetime).getTime()) / 
                      (1000 * 60 * 60)
                    )} hours
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Registered:</span>
                  <span className="font-medium">{event.registration_count} people</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
              
              <div className="space-y-3">
                <button
                  onClick={() => {
                    const startDate = new Date(event.start_datetime);
                    const endDate = new Date(event.end_datetime);
                    
                    const icsContent = [
                      'BEGIN:VCALENDAR',
                      'VERSION:2.0',
                      'PRODID:-//Baseball Calendar//EN',
                      'BEGIN:VEVENT',
                      `DTSTART:${startDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
                      `DTEND:${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
                      `SUMMARY:${event.title}`,
                      `DESCRIPTION:${event.description}`,
                      event.location ? `LOCATION:${event.location}` : '',
                      'END:VEVENT',
                      'END:VCALENDAR'
                    ].filter(Boolean).join('\r\n');
                    
                    const blob = new Blob([icsContent], { type: 'text/calendar' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${event.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
                    link.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm flex items-center justify-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  Add to Calendar
                </button>
                
                <button
                  onClick={() => {
                    const shareText = `Check out this event: ${event.title}\n${event.description}\n\nDate: ${event.formatted_date_range}${event.location ? `\nLocation: ${event.location}` : ''}`;
                    
                    if (navigator.share) {
                      navigator.share({
                        title: event.title,
                        text: shareText,
                        url: window.location.href,
                      });
                    } else {
                      navigator.clipboard.writeText(`${shareText}\n\n${window.location.href}`);
                      toast.success('Event details copied to clipboard');
                    }
                  }}
                  className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  Share Event
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Registration Modal */}
        {showRegistrationForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Event Registration</h2>
                
                <form onSubmit={handleSubmitRegistration} className="space-y-4">
                  {/* Participant Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="participant_name"
                        value={form.participant_name}
                        onChange={handleInputChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="participant_email"
                        value={form.participant_email}
                        onChange={handleInputChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="participant_phone"
                        value={form.participant_phone}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date of Birth *
                      </label>
                      <input
                        type="date"
                        name="date_of_birth"
                        value={form.date_of_birth}
                        onChange={handleInputChange}
                        required
                        max={new Date().toISOString().split('T')[0]}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Guardian Information (for minors) */}
                  {isMinor && (
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h3 className="text-lg font-medium text-gray-900 mb-3">
                        Guardian Information (Required for participants under 18)
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Guardian Name *
                          </label>
                          <input
                            type="text"
                            name="guardian_name"
                            value={form.guardian_name}
                            onChange={handleInputChange}
                            required={isMinor}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Guardian Phone *
                          </label>
                          <input
                            type="tel"
                            name="guardian_phone"
                            value={form.guardian_phone}
                            onChange={handleInputChange}
                            required={isMinor}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Emergency Information */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emergency Contact *
                    </label>
                    <textarea
                      name="emergency_contact"
                      value={form.emergency_contact}
                      onChange={handleInputChange}
                      required
                      rows={2}
                      placeholder="Name, relationship, and phone number of emergency contact"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Notes
                    </label>
                    <textarea
                      name="notes"
                      value={form.notes}
                      onChange={handleInputChange}
                      rows={2}
                      placeholder="Any additional information or special requests"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex gap-4 pt-6">
                    <button
                      type="button"
                      onClick={() => setShowRegistrationForm(false)}
                      className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={registering}
                      className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {registering ? 'Registering...' : 'Register'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EventDetailPage;